import { useState, useEffect, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import CookieConsent from '../components/CookieConsent';
import AnnouncementPopup from '../components/AnnouncementPopup';
import { useAuth } from '../contexts/AuthContext';

const API_BASE = process.env.REACT_APP_API_BASE || '/.netlify/functions/api';
const DIRECTORY_URL = `${API_BASE}/directory`;
const DIR_RESOLVE_URL = `${API_BASE}/directory/resolve`;
const TOP_WORST_URL = `${API_BASE}/doctors/topworst?limit=4`;
const TOP_WORST_COURSES_URL = `${API_BASE}/courses/topworst?limit=4`;
const LATEST_REVIEWS_CANDIDATES = [
  (limit) => `${API_BASE}/reviews/latest?limit=${limit}`,
  (limit) => `${API_BASE}/reviews/recent?limit=${limit}`,
  (limit) => `${API_BASE}/reviews?sort=created_at_desc&limit=${limit}`,
];

const HomePage = () => {
  const navigate = useNavigate();
  const { user } = useAuth() || {};

  const userName =
    user?.full_name ||
    user?.name ||
    user?.username ||
    (user?.email ? user.email.split('@')[0] : '');

  const [topLecturers, setTopLecturers] = useState([]);
  const [worstLecturers, setWorstLecturers] = useState([]);
  const [bestCourses, setBestCourses] = useState([]);
  const [worstCourses, setWorstCourses] = useState([]);
  const [allDoctors, setAllDoctors] = useState([]);
  const [loading, setLoading] = useState(true);

  const [selectedLecturerFilterTop, setSelectedLecturerFilterTop] = useState('الجميع');
  const [selectedLecturerFilterWorst, setSelectedLecturerFilterWorst] = useState('الجميع');

  const [selectedCourseFilter, setSelectedCourseFilter] = useState('الجميع');
  const [currentSlide, setCurrentSlide] = useState(0);
  const [currentServiceSlide, setCurrentServiceSlide] = useState(0);

  const [testimonials, setTestimonials] = useState([]);
  const [currentTestimonialSlide, setCurrentTestimonialSlide] = useState(0);

  const [isMobile, setIsMobile] = useState(false);
  const [isSliderPaused, setIsSliderPaused] = useState(false);
  const [isServiceSliderPaused, setIsServiceSliderPaused] = useState(false);
  const [isTestimonialSliderPaused, setIsTestimonialSliderPaused] = useState(false);

  const [searchQuery, setSearchQuery] = useState('');
  const [showSuggestions, setShowSuggestions] = useState(false);
  const [searchSuggestions, setSearchSuggestions] = useState([]);

  const [barPct, setBarPct] = useState(0);

  const fetchJSON = async (url) => {
    const res = await fetch(url, { headers: { Accept: 'application/json' } });
    const text = await res.text();
    if (!res.ok) throw new Error(text || `HTTP ${res.status}`);
    return JSON.parse(text);
  };

  useEffect(() => {
    let mounted = true;
    (async () => {
      try {
        const [topWorst, directory, topWorstCourses] = await Promise.all([
          fetchJSON(TOP_WORST_URL),
          fetchJSON(DIRECTORY_URL),
          fetchJSON(TOP_WORST_COURSES_URL).catch(() => ({ best: [], worst: [] })),
        ]);
        if (!mounted) return;

        const normalizeRated = (d, idx) => {
          const name = d.Doctorname || d.name || '';
          return {
            name,
            college: d.college || '',
            rating: Number.isFinite(d.avg_rating) ? d.avg_rating : Number(d.rating || 0),
            evaluation: '',
            id: d.ID || name || `lecturer-${idx}`,
          };
        };

        const dedupeById = (arr) => {
          const seen = new Set();
          return arr.filter((item) => {
            if (seen.has(item.id)) return false;
            seen.add(item.id);
            return true;
          });
        };

        const best = dedupeById(Array.isArray(topWorst?.best) ? topWorst.best.map(normalizeRated) : []);
        const worst = dedupeById(Array.isArray(topWorst?.worst) ? topWorst.worst.map(normalizeRated) : []);

        setTopLecturers(best);
        setWorstLecturers(worst);

        const normalizeCourse = (c, idx) => {
          const name = c.name || '';
          const code = c.code || '';
          return {
            name,
            code,
            college: '',
            rating: Number.isFinite(Number(c.avg_rating)) ? Number(c.avg_rating) : 0,
            review_count: Number(c.review_count) || 0,
            id: code || name || `course-${idx}`,
          };
        };

        setBestCourses(dedupeById(Array.isArray(topWorstCourses?.best) ? topWorstCourses.best.map(normalizeCourse) : []));
        setWorstCourses(dedupeById(Array.isArray(topWorstCourses?.worst) ? topWorstCourses.worst.map(normalizeCourse) : []));

        const all = Array.isArray(directory)
          ? directory.map((d, idx) => ({
              name: d.name || '',
              college: d.college || '',
              id: d.ID || `${d.name}-${idx}`,
            }))
          : [];
        setAllDoctors(all);
      } catch {
        setTopLecturers([]);
        setWorstLecturers([]);
        setBestCourses([]);
        setWorstCourses([]);
        setAllDoctors([]);
      } finally {
        if (mounted) setLoading(false);
      }
    })();
    return () => {
      mounted = false;
    };
  }, []);

  useEffect(() => {
    let mounted = true;

    const toNumber = (v, fallback = 0) => {
      const n = Number(v);
      return Number.isFinite(n) ? n : fallback;
    };

    const clamp01 = (x) => Math.min(1, Math.max(0, x));

    const normalizeReview = (r) => {
      const text =
        r.reviewtext ||
        r.text ||
        r.comment ||
        r.content ||
        r.body ||
        r.review ||
        'تقييم بدون نص.';
      const name = r.Doctorname || r.doctorname || r.lecturerName || r.name || 'طالب';
      const college = r.college || r.College || r.department || '—';
      let rating =
        toNumber(r.overallRating) ||
        toNumber(r.avg_rating) ||
        toNumber(r.rating) ||
        (r.stars ? clamp01(Number(r.stars) / 5) * 100 : 0);
      if (!rating || rating < 0) rating = 80;
      rating = Math.max(0, Math.min(100, Math.round(rating)));
      return { text, name, college, rating };
    };

    const fetchLatest = async (limit = 3) => {
      for (const makeUrl of LATEST_REVIEWS_CANDIDATES) {
        const url = makeUrl(limit);
        try {
          const data = await fetchJSON(url);
          const list = Array.isArray(data) ? data : Array.isArray(data?.items) ? data.items : [];
          if (list.length) return list.slice(0, limit).map(normalizeReview);
        } catch {

        }
      }
      return [];
    };

    (async () => {
      const latest = await fetchLatest(3);
      if (!mounted) return;
      if (latest.length) setTestimonials(latest);
      else
        setTestimonials([
          {
            text: 'شرح واضح ومباشر، المادة صارت أسهل بكثير.',
            name: 'طالب مجهول',
            college: '—',
            rating: 88,
          },
          {
            text: 'تنظيم رائع واختبارات عادلة.',
            name: 'طالبة',
            college: '—',
            rating: 92,
          },
          {
            text: 'أسلوب مبسط وأمثلة كثيرة تساعد على الفهم.',
            name: 'Student',
            college: '—',
            rating: 85,
          },
        ]);
    })();

    return () => {
      mounted = false;
    };
  }, []);

  useEffect(() => {
    if (!testimonials.length) {
      setBarPct(80);
      return;
    }
    const target = Math.max(
      20,
      Math.min(100, Number(testimonials[currentTestimonialSlide]?.rating || 0))
    );
    let raf;
    const duration = 900;
    const start = performance.now();
    const from = 0;
    const to = target;
    const ease = (t) => (t < 0.5 ? 2 * t * t : -1 + (4 - 2 * t) * t);
    const tick = (now) => {
      const p = Math.min(1, (now - start) / duration);
      const v = from + (to - from) * ease(p);
      setBarPct(v);
      if (p < 1) raf = requestAnimationFrame(tick);
    };
    setBarPct(0);
    raf = requestAnimationFrame(tick);
    return () => cancelAnimationFrame(raf);
  }, [currentTestimonialSlide, testimonials]);

  const collegesTop = useMemo(() => {
    const s = new Set(topLecturers.map((d) => d.college).filter(Boolean));
    return ['الجميع', ...Array.from(s)];
  }, [topLecturers]);

  const collegesWorst = useMemo(() => {
    const s = new Set(worstLecturers.map((d) => d.college).filter(Boolean));
    return ['الجميع', ...Array.from(s)];
  }, [worstLecturers]);

  const filterByCollege = (items, selected) => {
    if (selected === 'الجميع') return items;
    return items.filter((x) => x.college === selected);
  };



  const cleanQuery = (raw) => {
    const text = typeof raw === 'string' ? raw : String(raw || '');
    return text
      .replace(/<[^>]*>/g, '')
      .replace(/[<>{}\[\];()\/\\`"'=:+]/g, '')
      .replace(/\s+/g, ' ')
      .slice(0, 64);
  };

  const stripArabicMarks = (s = '') =>
    s
      .replace(/[\u0617-\u061A\u064B-\u0652\u0670]/g, '')
      .replace(/\u0640/g, '');

  const arVariants = (s = '') =>
    s
      .replace(/[إأآا]/g, 'ا')
      .replace(/ى/g, 'ي')
      .replace(/ؤ/g, 'و')
      .replace(/ئ/g, 'ي')
      .replace(/ة/g, 'ه');

  const arNorm = (s = '') =>
    arVariants(stripArabicMarks(String(s).trim().toLowerCase()))
      .replace(/[^0-9a-z\u0600-\u06FF\s-]/gi, '')
      .replace(/-/g, ' ')
      .replace(/\s+/g, ' ');

  const tokenize = (s = '') => arNorm(s).split(' ').filter(Boolean);

  const buildIndex = (list) =>
    (Array.isArray(list) ? list : []).map((d, i) => ({
      id: d.id ?? `${d.name || 'unknown'}-${i}`,
      name: d.name || '',
      nameN: arNorm(d.name || ''),
      tokens: tokenize(d.name || ''),
      college: d.college || '—',
    }));

  const scoreMatch = (queryTokens, name) => {
    const row = arNorm(name || '');
    if (!row || !queryTokens.length) return -1;

    let score = 0;
    for (const t of queryTokens) {
      if (!row.includes(t)) {
        return -1;
      }
      if (row === t) score += 5;
      else if (row.startsWith(t + ' ') || row.endsWith(' ' + t)) score += 4;
      else if (row.startsWith(t)) score += 3;
      else score += 1;
    }
    return score;
  };

  const openDoctorByQuery = async (raw) => {
    const q = cleanQuery(raw).trim();
    if (!q) return;
    try {
      const resolved = await fetchJSON(`${DIR_RESOLVE_URL}?q=${encodeURIComponent(q)}`);
      if (resolved && resolved.id) {
        navigate(`/doctor-reviews/${resolved.id}`);
        return;
      }
    } catch {

    }
    const qn = arNorm(q);
    const local =
      allDoctors.find((d) => arNorm(d.name) === qn) ||
      allDoctors.find((d) => arNorm(d.name).startsWith(qn));
    if (local && local.id) {
      navigate(`/doctor-reviews/${local.id}`);
      return;
    }
    navigate(`/lecturer-evaluation?doctor=${encodeURIComponent(q)}`);
  };

  const handleSearch = (raw) => {
    const query = cleanQuery(raw);
    setSearchQuery(query);

    const qTrim = query.trim();
    if (qTrim.length < 1) {
      setSearchSuggestions([]);
      setShowSuggestions(false);
      return;
    }

    const qTokens = tokenize(qTrim);
    if (!qTokens.length) {
      setSearchSuggestions([]);
      setShowSuggestions(false);
      return;
    }

    const idx = buildIndex(allDoctors);

    const ranked = idx
      .map((d) => ({
        ...d,
        _score: scoreMatch(qTokens, d.name),
      }))
      .filter((d) => d._score >= 0)
      .sort((a, b) => b._score - a._score || a.name.localeCompare(b.name, 'ar'))
      .slice(0, 8)
      .map((d) => ({
        id: d.id,
        type: 'lecturer',
        displayName: d.name,
        college: d.college,
        _score: d._score,
      }));

    if (/(حاسبة|معدل|غياب|نسبة)/.test(qTrim)) {
      ranked.push({
        id: 'calc-1',
        type: 'calculator',
        displayName: 'حاسبة المعدل التراكمي',
        category: 'الحاسبة',
        _score: 0,
      });
      ranked.push({
        id: 'calc-2',
        type: 'calculator',
        displayName: 'حاسبة الغياب',
        category: 'الحاسبة',
        _score: 0,
      });
      ranked.push({
        id: 'calc-3',
        type: 'calculator',
        displayName: 'حاسبة النسب',
        category: 'الحاسبة',
        _score: 0,
      });
    }

    setSearchSuggestions(ranked);
    setShowSuggestions(ranked.length > 0);
  };

  const handleSuggestionClick = async (suggestion) => {
    if (suggestion.type === 'lecturer') {
      await openDoctorByQuery(suggestion.displayName);
    } else if (suggestion.type === 'course') {
      navigate('/course-evaluation');
    } else if (suggestion.type === 'calculator') {
      if (suggestion.id === 'calc-1') navigate('/calculator?type=gpa');
      else if (suggestion.id === 'calc-2') navigate('/calculator?type=absence');
      else if (suggestion.id === 'calc-3') navigate('/calculator?type=percentage');
    }
    setSearchQuery('');
    setShowSuggestions(false);
  };

  const handleSearchKeyDown = async (e) => {
    if (e.key === 'Enter') {
      if (searchSuggestions.length > 0 && searchSuggestions[0].type === 'lecturer') {
        await handleSuggestionClick(searchSuggestions[0]);
      } else {
        await openDoctorByQuery(searchQuery);
      }
    }
  };

  const handleSearchBlur = () => setTimeout(() => setShowSuggestions(false), 200);

  useEffect(() => {
    const check = () => setIsMobile(window.innerWidth < 768);
    check();
    window.addEventListener('resize', check);
    return () => window.removeEventListener('resize', check);
  }, []);

  useEffect(() => {
    setCurrentSlide(0);
  }, [isMobile]);

  useEffect(() => {
    setCurrentServiceSlide(0);
    setCurrentTestimonialSlide(0);
  }, [isMobile]);

  const sliderCards = [
    {
      key: 'best-lecturers',
      title: 'أفضل المحاضرين تقييما',
      type: 'lecturers-best',
      color: 'teal',
      data: filterByCollege(topLecturers, selectedLecturerFilterTop),
      colleges: collegesTop,
      filter: selectedLecturerFilterTop,
      setFilter: setSelectedLecturerFilterTop,
    },
    {
      key: 'worst-lecturers',
      title: 'أقل المحاضرين تقييما',
      type: 'lecturers-worst',
      color: 'red',
      data: filterByCollege(worstLecturers, selectedLecturerFilterWorst),
      colleges: collegesWorst,
      filter: selectedLecturerFilterWorst,
      setFilter: setSelectedLecturerFilterWorst,
    },
    {
      key: 'best-courses',
      title: 'أفضل المقررات تقييما',
      type: 'courses',
      color: 'teal',
      data: bestCourses,
      colleges: ['الجميع'],
      filter: selectedCourseFilter,
      setFilter: setSelectedCourseFilter,
    },
    {
      key: 'worst-courses',
      title: 'أقل المقررات تقييما',
      type: 'courses',
      color: 'red',
      data: worstCourses,
      colleges: ['الجميع'],
      filter: selectedCourseFilter,
      setFilter: setSelectedCourseFilter,
    },
  ];

  const totalSlides = isMobile ? sliderCards.length - 1 : sliderCards.length - 2;

  const nextSlide = () => setCurrentSlide((p) => Math.min(p + 1, totalSlides));
  const prevSlide = () => setCurrentSlide((p) => Math.max(p - 1, 0));
  const goToSlide = (i) => setCurrentSlide(i);

  const pauseSlider = () => setIsSliderPaused(true);
  const resumeSlider = () => setIsSliderPaused(false);
  const pauseServiceSlider = () => setIsServiceSliderPaused(true);
  const resumeServiceSlider = () => setIsServiceSliderPaused(false);
  const pauseTestimonialSlider = () => setIsTestimonialSliderPaused(true);
  const resumeTestimonialSlider = () => setIsTestimonialSliderPaused(false);

  const nextServiceSlide = () => setCurrentServiceSlide((p) => (p + 1) % 2);
  const prevServiceSlide = () => setCurrentServiceSlide((p) => (p - 1 + 2) % 2);

  const nextTestimonialSlide = () =>
    setCurrentTestimonialSlide((p) => (p + 1) % Math.max(1, testimonials.length));
  const prevTestimonialSlide = () =>
    setCurrentTestimonialSlide(
      (p) => (p - 1 + Math.max(1, testimonials.length)) % Math.max(1, testimonials.length)
    );
  const goToTestimonialSlide = (i) => setCurrentTestimonialSlide(i);

  useEffect(() => {
    const id = setInterval(() => {
      if (!isSliderPaused) setCurrentSlide((prev) => (prev >= totalSlides ? 0 : prev + 1));
    }, 5000);
    return () => clearInterval(id);
  }, [totalSlides, isSliderPaused]);

  useEffect(() => {
    const id = setInterval(() => {
      if (!isServiceSliderPaused) setCurrentServiceSlide((p) => (p + 1) % 2);
    }, 4000);
    return () => clearInterval(id);
  }, [isServiceSliderPaused]);

  useEffect(() => {
    const id = setInterval(() => {
      if (!isTestimonialSliderPaused && testimonials.length > 0) {
        setCurrentTestimonialSlide((p) => (p + 1) % testimonials.length);
      }
    }, 6000);
    return () => clearInterval(id);
  }, [isTestimonialSliderPaused, testimonials.length]);

  return (
    <div className="text-white bg-transparent relative z-10">
      <CookieConsent />
      <AnnouncementPopup />

      {userName && (
        <div className="w-full px-4 pt-6 flex justify-end">
          <div className="flex items-center gap-3 text-right">

            <div className="leading-snug">
              <div className="font-semibold text-sm md:text-base">
              <span className="text-xl md:text-2xl" aria-hidden="true">👋 {userName} أهلاً

            </span>
              </div>
              <div className="text-xs md:text-sm text-white/80">
.مرحباً بعودتك إلى منصة همّة، تابع من حيث انتهيت وأكمل مسارك بثقة
              </div>
            </div>
          </div>
        </div>
      )}

      <div className="text-center pt-[72px] md:pt-[84px] pb-24 md:pb-32 px-4 relative z-10">
        <p className="text-[12px] md:text-[16px] mb-2">مساعدك في رحلتك</p>
        <h1 className="text-3xl md:text-6xl font-bold mb-6">نحو القمة</h1>
        <p className="text-[12px] md:text-[16px] max-w-[340px] mx-auto leading-6 mb-6">
          موقع مخصص لطلاب وطالبات جامعة جدة يساعدهم في رحلتهم الدراسية منذ 2020
        </p>
        <button
          type="button"
          onClick={() => window.open('https://x.com/Hemh_uj', '_blank', 'noopener,noreferrer')}
          className="figma-button mb-6 mx-auto"
          aria-label="تابعنا على إكس"
        >
          تابعنا على إكس
        </button>
        <div className="flex justify-center">
          <div className="relative w-full max-w-[340px]">
            <input
              type="text"
              placeholder="ابحث عن عضو تدريس ..."
              className="w-full pl-10 pr-4 py-2 rounded-[6px] text-sm bg-transparent border border-white/20 focus:outline-none focus:ring-2 focus:ring-[#28e3da] text-right"
              dir="rtl"
              value={searchQuery}
              onChange={(e) => handleSearch(e.target.value)}
              onKeyDown={handleSearchKeyDown}
              onBlur={handleSearchBlur}
              autoComplete="off"
              autoCorrect="off"
              spellCheck={false}
            />
            <svg
              className="absolute left-3 top-1/2 -translate-y-1/2 text-white"
              width="20"
              height="20"
              fill="none"
              stroke="currentColor"
              strokeWidth="2"
              strokeLinecap="round"
              strokeLinejoin="round"
              aria-hidden="true"
            >
              <path d="M21 21l-6-6M3 11a8 8 0 1116 0 8 8 0 01-16 0z" />
            </svg>

            {showSuggestions && searchSuggestions.length > 0 && (
              <div className="absolute top-full left-0 right-0 mt-1 bg-[#081423] border border-white/20 rounded-[6px] shadow-lg z-50 max-h-64 overflow-y-auto [&::-webkit-scrollbar]:hidden [-ms-overflow-style:none] [scrollbar-width:none]">
                {searchSuggestions.map((suggestion, index) => (
                  <div
                    key={`${suggestion.type}-${suggestion.id || index}`}
                    className="px-4 py-3 hover:bg-white/10 cursor-pointer transition-colors duration-200 border-b border-white/10 last:border-b-0"
                    onClick={async () => await handleSuggestionClick(suggestion)}
                  >
                    <div className="flex items-center gap-3">
                      <div
                        className="w-6 h-6 rounded-full flex items-center justify-center flex-shrink-0"
                        style={{
                          background:
                            'radial-gradient(43.64% 77.08% at 50% -10.42%, rgba(40, 227, 218, 0.5) 0%, rgba(40, 227, 218, 0) 100%), rgba(40, 227, 218, 0.25)',
                        }}
                      >
                        <svg
                          className="w-3 h-3 text-white"
                          fill="currentColor"
                          viewBox="0 0 20 20"
                          aria-hidden="true"
                        >
                          <path
                            fillRule="evenodd"
                            d="M10 9a3 3 0 100-6 3 3 0 000 6zm-7 9a7 7 0 1114 0H3z"
                            clipRule="evenodd"
                          />
                        </svg>
                      </div>
                      <div className="flex-1 text-right">
                        <div className="text-white text-sm font-medium">
                          {suggestion.displayName}
                        </div>
                        <div className="text-white/70 text-xs mt-1">
                          {suggestion.college || suggestion.category}
                        </div>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>
      </div>

      {             }
      <div className="px-4 pt-8 md:pt-12 pb-24 md:pb-32">
        <div className="text-center mb-8">
          <h2 className="text-[20px] md:text-[32px] font-bold mb-2">التقييم</h2>
        </div>

        <div className="relative max-w-6xl mx-auto">
          <div className="overflow-hidden">
            <div
              className="flex transition-transform duration-300 ease-in-out"
              style={{ transform: `translateX(-${currentSlide * (isMobile ? 100 : 50)}%)` }}
              onMouseEnter={pauseSlider}
              onMouseLeave={resumeSlider}
            >
              {sliderCards.map((card) => (
                <div key={card.key} className="w-full md:w-1/2 flex-shrink-0 px-3">
                  <div
                    className={`rounded-xl p-6 border h-[600px] flex flex-col ${
                      card.color === 'teal'
                        ? 'border-[rgba(40,227,218,0.25)]'
                        : 'border-[rgba(227,40,90,0.25)]'
                    }`}
                    style={{
                      background:
                        card.color === 'teal'
                          ? 'linear-gradient(0deg, rgba(255, 255, 255, 0.02), rgba(255, 255, 255, 0.02)), radial-gradient(92.12% 184.55% at 50% -55%, rgba(40, 227, 218, 0.2) 0%, rgba(40, 227, 218, 0) 100%), #081423'
                          : 'linear-gradient(0deg, rgba(255, 255, 255, 0.02), rgba(255, 255, 255, 0.02)), radial-gradient(92.12% 184.55% at 50% -55%, rgba(227, 40, 90, 0.2) 0%, rgba(227, 40, 90, 0) 100%), #081423',
                    }}
                  >
                    <h3
                      className="text-[20px] md:text-[24px] font-medium mb-4 text-center text-white"
                      style={{
                        background:
                          card.color === 'teal'
                            ? 'radial-gradient(43.64% 77.08% at 50% -10.42%, rgba(40, 227, 218, 0.5) 0%, rgba(40, 227, 218, 0) 100%), rgba(40, 227, 218, 0.25)'
                            : 'radial-gradient(43.64% 77.08% at 50% -10.42%, rgba(227, 40, 90, 0.5) 0%, rgba(227, 40, 90, 0) 100%), rgba(227, 40, 90, 0.25)',
                        borderRadius: '12px',
                        padding: '24px',
                        border:
                          card.color === 'teal'
                            ? '1px solid rgba(40, 227, 218, 0.25)'
                            : '1px solid rgba(227, 40, 90, 0.25)',
                      }}
                    >
                      {card.title}
                    </h3>

                    <div className="flex flex-wrap gap-2 mb-6 justify-center flex-row-reverse">
                      {card.colleges.map((college) => (
                        <button
                          key={`${card.key}-${college}`}
                          onClick={() => card.setFilter(college)}
                          className={`px-3 py-2 rounded-[12px] text-xs transition-colors ${
                            card.filter === college ? 'text-white font-medium' : 'text-white/70 hover:text-white'
                          }`}
                          style={{
                            background:
                              card.filter === college
                                ? card.color === 'teal'
                                  ? 'radial-gradient(43.64% 77.08% at 50% -10.42%, rgba(40, 227, 218, 0.5) 0%, rgba(40, 227, 218, 0) 100%), rgba(40, 227, 218, 0.25)'
                                  : 'radial-gradient(43.64% 77.08% at 50% -10.42%, rgba(227, 40, 90, 0.5) 0%, rgba(227, 40, 90, 0) 100%), rgba(227, 40, 90, 0.25)'
                                : 'transparent',
                          }}
                        >
                          {college}
                        </button>
                      ))}
                    </div>

                    <div className="space-y-3 flex-1 overflow-y-auto [&::-webkit-scrollbar]:hidden [-ms-overflow-style:none] [scrollbar-width:none]">
                      {loading && <div className="text-center text-white/70">جارِ التحميل...</div>}

                      {!loading &&
                        (card.type === 'lecturers-best' || card.type === 'lecturers-worst') &&
                        card.data.map((item) => (
                          <div
                            key={`${card.key}-${item.id}`}
                            className={`rounded-lg p-6 border ${
                              card.color === 'teal'
                                ? 'border-[rgba(40,227,218,0.25)]'
                                : 'border-[rgba(227,40,90,0.25)]'
                            }`}
                            style={{
                              background:
                                card.color === 'teal'
                                  ? 'linear-gradient(0deg, rgba(255, 255, 255, 0.02), rgba(255, 255, 255, 0.02)), radial-gradient(92.12% 184.55% at 50% -55%, rgba(40, 227, 218, 0.2) 0%, rgba(40, 227, 218, 0) 100%), #081423'
                                  : 'linear-gradient(0deg, rgba(255, 255, 255, 0.02), rgba(255, 255, 255, 0.02)), radial-gradient(92.12% 184.55% at 50% -55%, rgba(227, 40, 90, 0.2) 0%, rgba(227, 40, 90, 0) 100%), #081423',
                            }}
                          >
                            <div className="flex items-center justify-between gap-[22px]">
                              <div className="flex-1">
                                <button
                                  type="button"
                                  onClick={async () => await openDoctorByQuery(item.name)}
                                  className="text-white font-medium text-[14px] md:text-[16px] text-right hover:underline"
                                >
                                  {item.name}
                                </button>
                                <div className="text-[#D5D2D2] text-[12px] md:text-[14px] text-right">
                                  {item.evaluation || ''}
                                </div>
                                <div className="text-[#D5D2D2] text-[12px] md:text-[14px] text-right mt-1">
                                  {item.college || '—'}
                                </div>
                                <div className="flex items-center gap-2 mt-2">
                                  <span className="text-white font-medium text-[14px] md:text-[16px]">
                                    {item.rating}% تقييمه
                                  </span>
                                  <div
                                    className="flex-1 rounded-lg h-[10px]"
                                    style={{
                                      background:
                                        card.color === 'teal'
                                          ? 'linear-gradient(0deg, rgba(255, 255, 255, 0.02), rgba(255, 255, 255, 0.02)), radial-gradient(92.12% 184.55% at 50% -55%, rgba(40, 227, 218, 0.2) 0%, rgba(40, 227, 218, 0) 100%), #081423'
                                          : 'radial-gradient(43.64% 77.08% at 50% -10.42%, rgba(227, 40, 90, 0.5) 0%, rgba(227, 40, 90, 0) 100%), rgba(227, 40, 90, 0.25)',
                                      border: card.color === 'teal' ? '1px solid rgba(40, 227, 218, 0.25)' : 'none',
                                    }}
                                  >
                                    <div
                                      className="h-[10px] rounded-lg transition-all duration-300"
                                      style={{
                                        width: `${Math.max(0, Math.min(100, item.rating))}%`,
                                        marginLeft: 'auto',
                                        background:
                                          card.color === 'teal'
                                            ? 'radial-gradient(43.64% 77.08% at 50% -10.42%, rgba(40, 227, 218, 0.5) 0%, rgba(40, 227, 218, 0) 100%), rgba(40, 227, 218, 0.25)'
                                            : 'radial-gradient(43.64% 77.08% at 50% -10.42%, rgba(227, 40, 90, 0.5) 0%, rgba(227, 40, 90, 0) 100%), rgba(227, 40, 90, 0.25)',
                                      }}
                                    />
                                  </div>
                                </div>
                              </div>
                              <div
                                className="w-[42px] h-[42px] rounded-lg flex items-center justify-center"
                                style={{
                                  background:
                                    card.color === 'teal'
                                      ? 'radial-gradient(43.64% 77.08% at 50% -10.42%, rgba(40, 227, 218, 0.5) 0%, rgba(40, 227, 218, 0) 100%), rgba(40, 227, 218, 0.25)'
                                      : 'radial-gradient(43.64% 77.08% at 50% -10.42%, rgba(227, 40, 90, 0.5) 0%, rgba(227, 40, 90, 0) 100%), rgba(227, 40, 90, 0.25)',
                                }}
                              >
                                <svg
                                  className="w-[18px] h-[18px] text-white"
                                  fill="currentColor"
                                  viewBox="0 0 20 20"
                                  aria-hidden="true"
                                >
                                  <path
                                    fillRule="evenodd"
                                    d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z"
                                    clipRule="evenodd"
                                  />
                                </svg>
                              </div>
                            </div>
                          </div>
                        ))}

                      {!loading && card.type === 'courses' && card.data.length === 0 && (
                        <div className="text-center text-white/50">لا توجد بيانات</div>
                      )}

                      {!loading &&
                        card.type === 'courses' &&
                        card.data.map((item) => (
                          <div
                            key={`${card.key}-${item.id}`}
                            className={`rounded-lg p-6 border ${
                              card.color === 'teal'
                                ? 'border-[rgba(40,227,218,0.25)]'
                                : 'border-[rgba(227,40,90,0.25)]'
                            }`}
                            style={{
                              background:
                                card.color === 'teal'
                                  ? 'linear-gradient(0deg, rgba(255, 255, 255, 0.02), rgba(255, 255, 255, 0.02)), radial-gradient(92.12% 184.55% at 50% -55%, rgba(40, 227, 218, 0.2) 0%, rgba(40, 227, 218, 0) 100%), #081423'
                                  : 'linear-gradient(0deg, rgba(255, 255, 255, 0.02), rgba(255, 255, 255, 0.02)), radial-gradient(92.12% 184.55% at 50% -55%, rgba(227, 40, 90, 0.2) 0%, rgba(227, 40, 90, 0) 100%), #081423',
                            }}
                          >
                            <div className="flex items-center justify-between gap-[22px]">
                              <div className="flex-1">
                                <div className="text-white font-medium text-[14px] md:text-[16px] text-right">
                                  {item.name}
                                </div>
                                {item.code && (
                                  <div className="text-[#D5D2D2] text-[12px] md:text-[14px] text-right mt-0.5">
                                    {item.code}
                                  </div>
                                )}
                                <div className="flex items-center gap-2 mt-2">
                                  <span className="text-white font-medium text-[14px] md:text-[16px]">
                                    {Math.round(item.rating)}% تقييمه
                                  </span>
                                  <div
                                    className="flex-1 rounded-lg h-[10px]"
                                    style={{
                                      background:
                                        card.color === 'teal'
                                          ? 'linear-gradient(0deg, rgba(255, 255, 255, 0.02), rgba(255, 255, 255, 0.02)), radial-gradient(92.12% 184.55% at 50% -55%, rgba(40, 227, 218, 0.2) 0%, rgba(40, 227, 218, 0) 100%), #081423'
                                          : 'radial-gradient(43.64% 77.08% at 50% -10.42%, rgba(227, 40, 90, 0.5) 0%, rgba(227, 40, 90, 0) 100%), rgba(227, 40, 90, 0.25)',
                                      border: card.color === 'teal' ? '1px solid rgba(40, 227, 218, 0.25)' : 'none',
                                    }}
                                  >
                                    <div
                                      className="h-[10px] rounded-lg transition-all duration-300"
                                      style={{
                                        width: `${Math.max(0, Math.min(100, Math.round(item.rating)))}%`,
                                        marginLeft: 'auto',
                                        background:
                                          card.color === 'teal'
                                            ? 'radial-gradient(43.64% 77.08% at 50% -10.42%, rgba(40, 227, 218, 0.5) 0%, rgba(40, 227, 218, 0) 100%), rgba(40, 227, 218, 0.25)'
                                            : 'radial-gradient(43.64% 77.08% at 50% -10.42%, rgba(227, 40, 90, 0.5) 0%, rgba(227, 40, 90, 0) 100%), rgba(227, 40, 90, 0.25)',
                                      }}
                                    />
                                  </div>
                                </div>
                                {item.review_count > 0 && (
                                  <div className="text-white/50 text-[11px] text-right mt-1">
                                    {item.review_count} تقييم
                                  </div>
                                )}
                              </div>
                              <div
                                className="w-[42px] h-[42px] rounded-lg flex items-center justify-center flex-shrink-0"
                                style={{
                                  background:
                                    card.color === 'teal'
                                      ? 'radial-gradient(43.64% 77.08% at 50% -10.42%, rgba(40, 227, 218, 0.5) 0%, rgba(40, 227, 218, 0) 100%), rgba(40, 227, 218, 0.25)'
                                      : 'radial-gradient(43.64% 77.08% at 50% -10.42%, rgba(227, 40, 90, 0.5) 0%, rgba(227, 40, 90, 0) 100%), rgba(227, 40, 90, 0.25)',
                                }}
                              >
                                <svg
                                  className="w-[18px] h-[18px] text-white"
                                  fill="currentColor"
                                  viewBox="0 0 24 24"
                                  aria-hidden="true"
                                >
                                  <path d="M12 2L2 7l10 5 10-5-10-5zM2 17l10 5 10-5M2 12l10 5 10-5" stroke="currentColor" strokeWidth="2" fill="none" strokeLinecap="round" strokeLinejoin="round"/>
                                </svg>
                              </div>
                            </div>
                          </div>
                        ))}
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </div>

          <div className="flex justify-center items-center gap-4 mt-8 relative z-20">
            <div className="flex gap-2">
              {Array.from({ length: totalSlides + 1 }, (_, index) => (
                <button
                  key={index}
                  onClick={() => goToSlide(index)}
                  className={`w-2 h-1 rounded-full transition-colors relative z-20 ${
                    index === currentSlide ? 'bg-[#28e3da]' : 'bg-white/30'
                  }`}
                />
              ))}
            </div>
          </div>

          <div className="flex justify-center items-center gap-10 mt-8 relative z-20">
            <button
              onClick={prevSlide}
              disabled={currentSlide === 0}
              className={`w-[49px] h-[49px] rounded-full flex items-center justify-center transition-all relative z-20 ${
                currentSlide === 0 ? 'bg-white/20 cursor-not-allowed' : 'bg-white hover:bg-white/90'
              }`}
              aria-label="السابق"
            >
              <svg width="26" height="20" viewBox="0 0 26 20" fill="none" aria-hidden="true">
                <path
                  d="M25 10H1M1 10L10 1M1 10L10 19"
                  stroke="#081423"
                  strokeWidth="3"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                />
              </svg>
            </button>

            <button
              onClick={nextSlide}
              disabled={currentSlide === totalSlides}
              className={`w-[49px] h-[49px] rounded-full flex items-center justify-center transition-all relative z-20 ${
                currentSlide === totalSlides ? 'bg-white/20 cursor-not-allowed' : 'bg-white hover:bg-white/90'
              }`}
              aria-label="التالي"
            >
              <svg width="26" height="20" viewBox="0 0 26 20" fill="none" aria-hidden="true">
                <path
                  d="M1 10H25M25 10L16 1M25 10L16 19"
                  stroke="#081423"
                  strokeWidth="3"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                />
              </svg>
            </button>
          </div>
        </div>
      </div>

      <div className="px-4 pt-8 md:pt-12 pb-24 md:pb-32">
        <div className="text-center mb-8">
          <h2 className="text-[20px] md:text-[32px] font-bold mb-4">الخدمات الالكترونية</h2>
          <p className="text-[12px] md:text-[16px] text-white/80 max-w-[600px] mx-auto leading-6">
            نوفر لك مجموعة من الخدمات الالكترونية التي تسهل تجربتك الأكاديمية
          </p>
        </div>

        <div className="max-w-4xl mx-auto">
          <div className="relative overflow-hidden">
            <div
              className="flex transition-transform duration-300 ease-in-out gap-0 md:gap-4"
              style={{ transform: `translateX(-${currentServiceSlide * (isMobile ? 100 : 50)}%)` }}
              onMouseEnter={pauseServiceSlider}
              onMouseLeave={resumeServiceSlider}
            >
              <div className="w-full md:w-1/2 flex-shrink-0 px-1 md:px-2">
                <div
                  className="w-[180px] h-[180px] md:w-[222px] md:h-[229px] rounded-[10px] flex items-center justify-center relative mx-auto md:ml-auto md:mr-0"
                  style={{
                    background:
                      'linear-gradient(0deg, rgba(255, 255, 255, 0.02), rgba(255, 255, 255, 0.02)), radial-gradient(92.12% 184.55% at 50% -55%, rgba(40, 227, 218, 0.2) 0%, rgba(40, 227, 218, 0) 100%), #081423',
                    border: '0.823228px solid rgba(40, 227, 218, 0.25)',
                  }}
                >
                  <img src="/images/Bb.png" alt="Blackboard" className="w-[140px] h-[60px] md:w-[171px] md:h-[77px] object-contain" />
                </div>
              </div>

              <div className="w-full md:w-1/2 flex-shrink-0 px-1 md:px-2">
                <div
                  className="w-[180px] h-[180px] md:w-[222px] md:h-[229px] rounded-[10px] flex items-center justify-center relative mx-auto md:mr-auto md:ml-0"
                  style={{
                    background:
                      'linear-gradient(0deg, rgba(255, 255, 255, 0.02), rgba(255, 255, 255, 0.02)), radial-gradient(92.12% 184.55% at 50% -55%, rgba(40, 227, 218, 0.2) 0%, rgba(40, 227, 218, 0) 100%), #081423',
                    border: '0.823228px solid rgba(40, 227, 218, 0.25)',
                  }}
                >
                  <img src="/images/Wso2.png" alt="WSO2" className="w-[140px] h-[60px] md:w-[171px] md:h-[77px] object-contain" />
                </div>
              </div>
            </div>
          </div>

          <div className="flex justify-center items-center gap-12 md:gap-16 mt-4 md:mt-6 relative z-20">
            <button onClick={prevServiceSlide} aria-label="السابق" className="p-2 rounded focus:outline-none hover:opacity-80 transition relative z-20">
              <svg className="w-8 h-8 md:w-10 md:h-10" viewBox="0 0 24 24" fill="none" stroke="#E1E6EA" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
                <path d="M19 12H7" />
                <path d="M11 8L7 12L11 16" />
              </svg>
            </button>
            <button onClick={nextServiceSlide} aria-label="التالي" className="p-2 rounded focus:outline-none hover:opacity-80 transition relative z-20">
              <svg className="w-8 h-8 md:w-10 md:h-10" viewBox="0 0 24 24" fill="none" stroke="#E1E6EA" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
                <path d="M5 12h12" />
                <path d="M13 8L17 12L13 16" />
              </svg>
            </button>
          </div>
        </div>
      </div>

      <div className="px-4 pt-12 pb-28 hidden md:block">
        <div className="text-center mb-8">
          <h2 className="text-[20px] md:text-[32px] font-bold mb-4">الحاسبة</h2>
          <p className="text-[12px] md:text-[16px] text-white/80 max-w-[600px] mx-auto leading-6">تعرف على نسب غيابك ومعدلك التراكمي بكل سهولة</p>
        </div>

        <div className="max-w-6xl mx-auto">
          <div className="grid grid-cols-4 gap-6">
            <div
              onClick={() => navigate('/calculator?type=percentage')}
              className="flex flex-col justify-center items-center py-[20px] px-[40px] md:py-[30px] md:px-[60px] gap-[16px] md:gap-[22px] cursor-pointer transition-all duration-300 group"
              style={{
                background:
                  'linear-gradient(0deg, rgba(255, 255, 255, 0.02), rgba(255, 255, 255, 0.02)), radial-gradient(92.12% 184.55% at 50% -55%, rgba(40, 227, 218, 0.2) 0%, rgba(40, 227, 218, 0) 100%), #081423',
                border: '1px solid rgba(40, 227, 218, 0.25)',
                borderRadius: '12px',
              }}
            >
              <div className="w-6 h-6 md:w-8 md:h-8 relative">
                <div className="absolute inset-0 flex items-center justify-center">
                  <div className="w-4 h-4 md:w-6 md:h-6 border-2 border-[#F5FBFF] rounded-full"></div>
                </div>
                <div className="absolute inset-0 flex items-center justify-center">
                  <div className="w-2 h-2 md:w-3 md:h-3 border-2 border-[#F5FBFF] rounded-full"></div>
                </div>
                <div className="absolute inset-0 flex items-center justify-center">
                  <div className="w-1 h-1 border-2 border-[#F5FBFF] rounded-full"></div>
                </div>
              </div>
              <div className="text-[#E1E6EA] font-bold text-[12px] md:text-[14px] leading-[17px] text-center">نسب المعدل</div>
            </div>

            <div
              onClick={() => navigate('/calculator?type=health')}
              className="flex flex-col justify-center items-center py-[20px] px-[40px] md:py-[30px] md:px-[60px] gap-[16px] md:gap-[22px] cursor-pointer transition-all duration-300 group"
              style={{
                background:
                  'linear-gradient(0deg, rgba(255, 255, 255, 0.02), rgba(255, 255, 255, 0.02)), radial-gradient(92.12% 184.55% at 50% -55%, rgba(40, 227, 218, 0.2) 0%, rgba(40, 227, 218, 0) 100%), #081423',
                border: '1px solid rgba(40, 227, 218, 0.25)',
                borderRadius: '12px',
              }}
            >
              <div className="w-6 h-6 md:w-8 md:h-8 flex items-center justify-center">
                <svg className="w-6 h-6 md:w-8 md:h-8 text-[#F5FBFF]" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M3 12h4l3-8 4 16 3-8h4" />
                </svg>
              </div>
              <div className="text-[#E1E6EA] font-bold text-[12px] md:text-[14px] leading-[17px] text-center">المسار الصحي</div>
            </div>

            <div
              onClick={() => navigate('/calculator?type=gpa')}
              className="flex flex-col justify-center items-center py-[20px] px-[40px] md:py-[30px] md:px-[60px] gap-[16px] md:gap-[22px] cursor-pointer transition-all duration-300 group"
              style={{
                background:
                  'linear-gradient(0deg, rgba(255, 255, 255, 0.02), rgba(255, 255, 255, 0.02)), radial-gradient(92.12% 184.55% at 50% -55%, rgba(40, 227, 218, 0.2) 0%, rgba(40, 227, 218, 0) 100%), #081423',
                border: '1px solid rgba(40, 227, 218, 0.25)',
                borderRadius: '12px',
              }}
            >
              <div className="w-6 h-6 md:w-8 md:h-8 flex items-center justify-center">
                <svg className="w-6 h-6 md:w-8 md:h-8 text-[#F5FBFF]" fill="currentColor" viewBox="0 0 24 24">
                  <path d="M3 13h2v-2H3v2zm0 4h2v-2H3v2zm0-8h2V7H3v2zm4 4h14v-2H7v2zm0 4h14v-2H7v2zM7 7v2h14V7H7z" />
                </svg>
              </div>
              <div className="text-[#E1E6EA] font-bold text-[12px] md:text-[14px] leading-[17px] text-center">المعدل التراكمي</div>
            </div>

            <div
              onClick={() => navigate('/calculator?type=absence')}
              className="flex flex-col justify-center items-center py-[20px] px-[40px] md:py-[30px] md:px-[60px] gap-[16px] md:gap-[22px] cursor-pointer transition-all duration-300 group"
              style={{
                background:
                  'linear-gradient(0deg, rgba(255, 255, 255, 0.02), rgba(255, 255, 255, 0.02)), radial-gradient(92.12% 184.55% at 50% -55%, rgba(40, 227, 218, 0.2) 0%, rgba(40, 227, 218, 0) 100%), #081423',
                border: '1px solid rgba(40, 227, 218, 0.25)',
                borderRadius: '12px',
              }}
            >
              <div className="w-6 h-6 md:w-8 md:h-8 flex items-center justify-center">
                <svg className="w-6 h-6 md:w-8 md:h-8 text-[#F5FBFF]" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z" />
                </svg>
              </div>
              <div className="text-[#E1E6EA] font-bold text-[12px] md:text-[14px] leading-[17px] text-center">الغياب</div>
            </div>
          </div>
        </div>
      </div>

      <div className="px-4 pt-8 md:pt-12 pb-24 md:pb-32">
        <div className="text-center mb-8">
          <h2 className="text-[20px] md:text-[32px] font-bold mb-4">آراء الطلاب</h2>
          <p className="text-[12px] md:text-[16px] text-white/80 max-w-[600px] mx-auto leading-6">تعرف على تجارب الطلاب مع المقررات الدراسية و المحاضرين</p>
        </div>

        <div className="w-full md:w-[894px] mx-auto">
          <div
            className="w-full md:w-[894px] mx-auto rounded-xl p-6 relative"
            style={{
              background:
                'linear-gradient(0deg, rgba(255, 255, 255, 0.02), rgba(255, 255, 255, 0.02)), radial-gradient(92.12% 184.55% at 50% -55%, rgba(40, 227, 218, 0.2) 0%, rgba(40, 227, 218, 0) 100%), #081423',
              border: '0.823228px solid rgba(40, 227, 218, 0.25)',
            }}
            onMouseEnter={() => setIsTestimonialSliderPaused(true)}
            onMouseLeave={() => setIsTestimonialSliderPaused(false)}
          >
            <div className="flex flex-row justify-center items-center gap-[15px] w-full h-[52px]">
              <div className="w-[50px] md:w-[64px] h-[52px] flex items-center justify-center text-center font-[Alexandria] font-medium text-[16px] md:text-[20px] leading-[52px] text-[#D5D2D2]">
                التقييم
              </div>

              <div className="w-[280px] md:w-[341px] h-[10px] relative" aria-hidden="true">
                <div
                  className="absolute inset-0 rounded-[12px] border border-[rgba(40,227,218,0.25)]"
                  style={{
                    background:
                      'linear-gradient(0deg, rgba(255, 255, 255, 0.02), rgba(255, 255, 255, 0.02)), radial-gradient(92.12% 184.55% at 50% -55%, rgba(40, 227, 218, 0.2) 0%, rgba(40, 227, 218, 0) 100%), #081423',
                  }}
                />
                <div
                  className="absolute right-0 top-0 h-[10px] rounded-[12px]"
                  style={{
                    width: `${testimonials.length === 0 ? 80 : Math.max(20, Math.min(100, barPct))}%`,
                    transition: 'width 80ms linear',
                    background:
                      'radial-gradient(43.64% 77.08% at 50% -10.42%, rgba(40, 227, 218, 0.5) 0%, rgba(40, 227, 218, 0) 100%), rgba(40, 227, 218, 0.25)',
                    boxShadow: 'inset 0 0 10px rgba(40,227,218,0.3)',
                  }}
                />
                <div
                  className="absolute top-1/2 -translate-y-1/2 w-3 h-3 rounded-full pointer-events-none"
                  style={{
                    right: `calc(${testimonials.length === 0 ? 80 : Math.max(20, Math.min(100, barPct))}% - 6px)`,
                    background:
                      'radial-gradient(circle at center, rgba(40,227,218,0.95) 0%, rgba(40,227,218,0.8) 35%, rgba(40,227,218,0) 70%)',
                    filter: 'drop-shadow(0 0 6px rgba(40,227,218,0.9))',
                    transition: 'right 80ms linear',
                  }}
                />
              </div>

              <div className="w-[40px] md:w-[52px] h-[52px] flex items-center justify-center text-center font-[Alexandria] font-medium text-[16px] md:text-[20px] leading-[52px] text-[#D5D2D2]">
                الشرح
              </div>
            </div>

            <div className="text-white text-center mb-6 leading-6 min-h-[80px] flex items-center justify-center relative px-4">
              {testimonials.map((t, i) => (
                <p
                  key={`t-body-${i}`}
                  className={`text-sm md:text-base italic absolute inset-0 flex items-center justify-center transition-all duration-1000 ease-in-out px-2 ${currentTestimonialSlide === i ? 'opacity-100 scale-100' : 'opacity-0 scale-95'}`}
                >
                  “{t.text}”
                </p>
              ))}
              {testimonials.length === 0 && <p className="text-sm md:text-base italic">“لا توجد تقييمات بعد.”</p>}
            </div>

            <div className="text-center mb-6 relative h-12">
              {testimonials.map((t, i) => (
                <div key={`t-name-${i}`} className={`absolute inset-0 transition-all duration-1000 ease-in-out ${currentTestimonialSlide === i ? 'opacity-100 scale-100' : 'opacity-0 scale-95'}`}>
                  <div className="text-white font-medium text-sm md:text-base">{t.name}</div>
                  <div className="text-white/70 text-xs md:text-sm" style={{ marginTop: '0.25rem' }}>{t.college}</div>
                </div>
              ))}
            </div>

            <div className="flex justify-center items-center gap-2 mt-4 relative z-20">
              {(testimonials.length ? testimonials : new Array(3).fill(0)).map((_, index) => (
                <button
                  key={index}
                  onClick={() => goToTestimonialSlide(index)}
                  className={`w-2 h-1 rounded-full transition-colors relative z-20 ${index === currentTestimonialSlide ? 'bg-[#28e3da]' : 'bg-white/30'}`}
                  aria-label={`اذهب للتقييم رقم ${index + 1}`}
                />
              ))}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default HomePage;
